import { createSlice, PayloadAction } from "@reduxjs/toolkit";
import { format, formatISO } from "date-fns";
import { DENTAL_PROCEDURES, TPLO_PROCEDURE } from "duck/constants";
import {
  getSpeciesTeeth,
  hasToothDeepPockets,
  isToothFilled,
  getEmptyBoneLoss
} from "duck/utils/cases";
import generateId from "duck/utils/generateId";
import {
  CaseStatusEnum,
  Fragments,
  PatientStateEnum,
  SpeciesEnum,
  ToothStateEnum
} from "requests";
import {
  CAT_TESTS,
  CAT_VACCINES,
  DOG_TESTS,
  DOG_VACCINES,
  EMPTY_CASE,
  EMPTY_DENTAL_CHARTING,
  EMPTY_GENERAL_ASSESSMENT
} from "./constants";
import * as Types from "./types";

export type CasesStateType = Record<
  string,
  {
    /*
     * 'offline' if a case was created in the offline mode
     * 'has_offline_changes' if a real case has any changes that need to be published
     * */
    status: Fragments.StoredMedicalCaseOfflineStatus;

    /* shouldSync=true if network requests should be done  */
    shouldSync: boolean;

    userId: number;
    data: Fragments.StoredMedicalCase;
  }
>;

const casesSlice = createSlice({
  name: "cases",
  initialState: {} as CasesStateType,
  reducers: {
    removeCase(state, { payload: id }: PayloadAction<string | number>) {
      delete state[id];
    },
    initOffline(
      state,
      {
        payload: {
          userId,
          name,
          doctor,
          id,
          authorizations,
          disclaimerText,
          disclaimerProcedures
        }
      }: PayloadAction<Types.InitOfflinePayload>
    ) {
      const createdAt = formatISO(new Date());

      state[id] = {
        status: "offline",
        shouldSync: false,
        userId,
        data: {
          _id: id,
          name,
          createdAt,
          status: CaseStatusEnum.Active,
          fromVetspire: false,
          disclaimerText,
          authorizations,
          disclaimerProcedures,
          client: {
            ...EMPTY_CASE.client,
            patient: {
              ...EMPTY_CASE.client.patient,
              name,
              procedureDate: createdAt,
              ...(doctor !== null
                ? {
                    dropOff: {
                      ...EMPTY_CASE.client.patient.dropOff,
                      doctor,
                      evaluateTestAndTreat: true
                    },
                    diagnosticTesting: {
                      ...EMPTY_CASE.client.patient.diagnosticTesting,
                      reviewedByDvm: false,
                      doctor
                    }
                  }
                : {})
            }
          } as any
        }
      };
    },

    initOnline(
      state,
      {
        payload: { case: newCase, userId }
      }: PayloadAction<Types.InitOnlinePayload>
    ) {
      const patientInfo = newCase.client.patient;

      state[newCase._id] = {
        status: "normal",
        shouldSync: true,
        userId,
        data: {} as any
      };

      state[newCase._id].data = {
        ...newCase,
        authorizations: newCase.authorizations?.collection || [],
        disclaimerProcedures: newCase.disclaimerProcedures?.collection || [],
        client: {
          ...newCase.client,
          disclaimer: {
            ...newCase.client.disclaimer,
            signature: newCase.client.disclaimer.signature?._id || null
          },
          state: newCase.client.state?._id || null,
          city: newCase.client.city?._id || null,
          zipCode: newCase.client.zipCode || null,
          patient: {
            ...patientInfo,
            image: patientInfo.image?._id || null,
            breed: patientInfo.breed || null,
            monitoring: patientInfo.monitoring as any,
            patientProcedures: patientInfo.patientProcedures?.collection || [],
            fluidTemperatures: patientInfo.fluidTemperatures?.collection || [],
            anestheticGases: patientInfo.anestheticGases?.collection || [],
            vitals: patientInfo.vitals?.collection || [],
            vitalComment: patientInfo.vitalComment,
            dropOff: {
              ...patientInfo.dropOff,
              doctor: patientInfo.dropOff.doctor?._id || null
            },
            vaccines: patientInfo.vaccines?.collection || [],
            patientTests: patientInfo.patientTests?.collection || [],
            treatment: patientInfo.treatment || null,

            diagnosticTesting: {
              ...patientInfo.diagnosticTesting,
              doctor: patientInfo.diagnosticTesting.doctor?._id || null,
              additionalTests:
                patientInfo.diagnosticTesting.additionalTests?.collection || []
            },

            medicationCalculations:
              patientInfo.medicationCalculations?.collection || [],

            // TODO: init correctly
            miscellaneousGroup: patientInfo.miscellaneousGroup as any,
            defibMedications: patientInfo.defibMedications?.collection || [],
            fluidBolus: patientInfo.fluidBolus || null,
            regionalBlocks: patientInfo.regionalBlocks?.collection || [],
            calculationAgreement: patientInfo.calculationAgreement
              ? {
                  ...patientInfo.calculationAgreement,
                  file: patientInfo.calculationAgreement?.file?._id || null
                }
              : null,

            tablets: patientInfo.tablets?.collection || [],
            suspensions: patientInfo.suspensions?.collection || [],
            tibialPlateauLevelingOsteotomyPlanning:
              patientInfo.tibialPlateauLevelingOsteotomyPlanning || null,
            postAnestheticVitals:
              patientInfo.postAnestheticVitals?.collection || [],
            postAnestheticVitalComment:
              patientInfo.postAnestheticVitalComment || null,
            generalAssessment: patientInfo.generalAssessment
              ? {
                  ...patientInfo.generalAssessment,
                  beforePhotoFront:
                    patientInfo.generalAssessment.beforePhotoFront?._id || null,
                  beforePhotoLeft:
                    patientInfo.generalAssessment.beforePhotoLeft?._id || null,
                  beforePhotoRight:
                    patientInfo.generalAssessment.beforePhotoRight?._id || null,
                  afterPhotoFront:
                    patientInfo.generalAssessment.afterPhotoFront?._id || null,
                  afterPhotoLeft:
                    patientInfo.generalAssessment.afterPhotoLeft?._id || null,
                  afterPhotoRight:
                    patientInfo.generalAssessment.afterPhotoRight?._id || null,
                  additionalPhotos:
                    patientInfo.generalAssessment.additionalPhotos?.collection.map(
                      photo => photo._id
                    ) || [],
                  radiographicFindingsPhotos:
                    patientInfo.generalAssessment.radiographicFindingsPhotos?.collection.map(
                      photo => photo._id
                    ) || []
                }
              : null,
            dentalChartings:
              patientInfo.dentalChartings?.collection?.map(chart => ({
                ...chart,
                tooth: chart.tooth._id
              })) || null,
            currentMedications:
              patientInfo.currentMedications?.collection || [],
            dentalSketchComments:
              patientInfo.dentalSketchComments?.collection || [],
            dentalSketch: patientInfo.dentalSketch?._id || null
          }
        }
      };
    },

    mergeRealtimeUpdate(
      state,
      { payload: { _id, data } }: PayloadAction<Types.MergeRealtimeUpdate>
    ) {
      const newCase = data;
      const patientInfo = data.client.patient;
      state[_id] = { ...state[_id] };

      state[_id].data = {
        ...newCase,
        authorizations: newCase.authorizations?.collection || [],
        disclaimerProcedures: newCase.disclaimerProcedures?.collection || [],
        client: {
          ...newCase.client,
          disclaimer: {
            ...newCase.client.disclaimer,
            signature: newCase.client.disclaimer.signature?._id || null
          },
          state: newCase.client.state?._id || null,
          city: newCase.client.city?._id || null,
          zipCode: newCase.client.zipCode || null,
          patient: {
            ...patientInfo,
            image: patientInfo.image?._id || null,
            breed: patientInfo.breed || null,
            monitoring: patientInfo.monitoring as any,
            patientProcedures: patientInfo.patientProcedures?.collection || [],
            fluidTemperatures: patientInfo.fluidTemperatures?.collection || [],
            anestheticGases: patientInfo.anestheticGases?.collection || [],
            vitals: patientInfo.vitals?.collection || [],
            vitalComment: patientInfo.vitalComment,
            dropOff: {
              ...patientInfo.dropOff,
              doctor: patientInfo.dropOff.doctor?._id || null
            },
            vaccines: patientInfo.vaccines?.collection || [],
            patientTests: patientInfo.patientTests?.collection || [],
            treatment: patientInfo.treatment || null,

            diagnosticTesting: {
              ...patientInfo.diagnosticTesting,
              doctor: patientInfo.diagnosticTesting.doctor?._id || null,
              additionalTests:
                patientInfo.diagnosticTesting.additionalTests?.collection || []
            },

            medicationCalculations:
              patientInfo.medicationCalculations?.collection || [],

            // TODO: init correctly
            miscellaneousGroup: patientInfo.miscellaneousGroup as any,
            defibMedications: patientInfo.defibMedications?.collection || [],
            fluidBolus: patientInfo.fluidBolus || null,
            regionalBlocks: patientInfo.regionalBlocks?.collection || [],
            calculationAgreement: patientInfo.calculationAgreement
              ? {
                  ...patientInfo.calculationAgreement,
                  file: patientInfo.calculationAgreement?.file?._id || null
                }
              : null,

            tablets: patientInfo.tablets?.collection || [],
            suspensions: patientInfo.suspensions?.collection || [],
            tibialPlateauLevelingOsteotomyPlanning:
              patientInfo.tibialPlateauLevelingOsteotomyPlanning || null,
            postAnestheticVitals:
              patientInfo.postAnestheticVitals?.collection || [],
            postAnestheticVitalComment:
              patientInfo.postAnestheticVitalComment || null,
            generalAssessment: patientInfo.generalAssessment
              ? {
                  ...patientInfo.generalAssessment,
                  beforePhotoFront:
                    patientInfo.generalAssessment.beforePhotoFront?._id || null,
                  beforePhotoLeft:
                    patientInfo.generalAssessment.beforePhotoLeft?._id || null,
                  beforePhotoRight:
                    patientInfo.generalAssessment.beforePhotoRight?._id || null,
                  afterPhotoFront:
                    patientInfo.generalAssessment.afterPhotoFront?._id || null,
                  afterPhotoLeft:
                    patientInfo.generalAssessment.afterPhotoLeft?._id || null,
                  afterPhotoRight:
                    patientInfo.generalAssessment.afterPhotoRight?._id || null,
                  additionalPhotos:
                    patientInfo.generalAssessment.additionalPhotos?.collection.map(
                      photo => photo._id
                    ) || [],
                  radiographicFindingsPhotos:
                    patientInfo.generalAssessment.radiographicFindingsPhotos?.collection.map(
                      photo => photo._id
                    ) || []
                }
              : null,
            dentalChartings:
              patientInfo.dentalChartings?.collection?.map(chart => ({
                ...chart,
                tooth: chart.tooth._id
              })) || null,
            currentMedications:
              patientInfo.currentMedications?.collection || [],
            dentalSketchComments:
              patientInfo.dentalSketchComments?.collection || [],
            dentalSketch: patientInfo.dentalSketch?._id || null
          }
        }
      };
    },

    updateCaseName(
      state,
      { payload: { _id, data } }: PayloadAction<Types.UpdateCaseNamePayload>
    ) {
      if (!state[_id]) {
        return;
      }
      state[_id].data.name = data || "";
    },

    updateCasesStatus(
      state,
      {
        payload: { status, _ids }
      }: PayloadAction<Types.UpdateCasesStatusPayload>
    ) {
      _ids.forEach(_id => {
        if (state[_id]) {
          state[_id].data.status = status;
        }
      });
    },

    deleteCases(state, { payload }: PayloadAction<Types.DeleteCasesPayload>) {
      payload.forEach(id => {
        delete state[id];
      });
    },

    updatePatientImage(
      state,
      { payload }: PayloadAction<Types.UpdatePatientImage>
    ) {
      state[payload._id].data.client.patient.image = payload.data;
      if (payload.data === null) {
        state[payload._id].data.client.patient.image = null;
      }
    },

    updatePatientInfo(
      state,
      {
        payload: {
          _id,
          data: { dateOfBirth, procedureDate, ...data }
        }
      }: PayloadAction<Types.UpdatePatientInfoPayload>
    ) {
      if (!state[_id]) {
        return;
      }

      state[_id].data.client.patient = {
        ...state[_id].data.client.patient,
        ...data
      };

      const { shouldSync } = state[_id];

      const { createdFrom } = state[_id].data;

      const patientInfo = state[_id].data.client.patient;
      const {
        weight,
        fluidBolus,
        generalAssessment,
        dentalChartings
      } = patientInfo;

      if (weight) {
        state[_id].data.client.patient.fluidBolus = {
          ...state[_id].data.client.patient.fluidBolus,
          ...(fluidBolus ? null : { fakeId: generateId() }),
          ...({ totalAmount: weight * 10 } as any)
        };

        state[_id].data.client.patient.medicationCalculations = state[
          _id
        ].data.client.patient.medicationCalculations.map(calc => {
          const totalAmount = calc.dose * weight;
          return {
            ...calc,
            totalAmount,
            totalVolume: calc.concentration
              ? totalAmount / calc.concentration
              : 0
          };
        });

        state[_id].data.client.patient.defibMedications = state[
          _id
        ].data.client.patient.defibMedications.map(defib => ({
          ...defib,
          dosageMin: defib.doseMin * weight,
          dosageMax: defib.doseMax * weight
        }));

        state[_id].data.client.patient.regionalBlocks = state[
          _id
        ].data.client.patient.regionalBlocks.map(block => ({
          ...block,
          dosageMin: block.doseMin * weight,
          dosageMax: block.doseMax * weight,
          volumeRangeMin: block.concentration
            ? (block.doseMin * weight) / block.concentration
            : 0,
          volumeRangeMax: block.concentration
            ? (block.doseMax * weight) / block.concentration
            : 0
        }));

        state[_id].data.client.patient.miscellaneousGroup.bagSize =
          (weight * 15 * 6) / 1000;

        const { fluidRate } = state[_id].data.client.patient.miscellaneousGroup;
        if (fluidRate != null) {
          state[_id].data.client.patient.miscellaneousGroup.fluidRateValue =
            weight * fluidRate;
        }

        state[_id].data.client.patient.tablets = state[
          _id
        ].data.client.patient.tablets.map(tablet => ({
          ...tablet,
          dosage: weight * tablet.dose
        }));

        state[_id].data.client.patient.suspensions = state[
          _id
        ].data.client.patient.suspensions.map(susp => ({
          ...susp,
          volume: susp.concentration
            ? (weight * susp.dose) / susp.concentration
            : 0
        }));
      }

      if (dateOfBirth !== undefined) {
        state[_id].data.client.patient.dateOfBirth =
          dateOfBirth === null
            ? null
            : format(new Date(dateOfBirth), "y-MM-dd");
      }

      if (procedureDate !== undefined) {
        state[_id].data.client.patient.procedureDate =
          procedureDate === null
            ? null
            : format(new Date(procedureDate), "y-MM-dd");
      }

      if (data.species !== undefined && !shouldSync) {
        state[_id].data.client.patient.vaccines = [];
        state[_id].data.client.patient.patientTests = [];
        state[_id].data.client.patient.treatment =
          data.species === SpeciesEnum.Cat || data.species === SpeciesEnum.Dog
            ? {
                fakeId: generateId(),
                microchip: false,
                laserTherapy: false,
                cereniaInjection: false
              }
            : null;
        state[_id].data.client.patient.dentalChartings = null;
        state[_id].data.client.patient.dentalSketchComments = [];
        state[_id].data.client.patient.dentalSketch = null;

        if (data.species === SpeciesEnum.Dog) {
          state[_id].data.client.patient.vaccines = DOG_VACCINES.map(
            vaccine => ({
              vaccine,
              toPerform: false,
              fakeId: generateId()
            })
          );

          state[_id].data.client.patient.patientTests = DOG_TESTS.map(
            value => ({
              test: value,
              toPerform: false,
              fakeId: generateId()
            })
          );
        }

        if (data.species === SpeciesEnum.Cat) {
          state[_id].data.client.patient.vaccines = CAT_VACCINES.map(
            vaccine => ({
              vaccine,
              toPerform: false,
              fakeId: generateId()
            })
          );

          state[_id].data.client.patient.patientTests = CAT_TESTS.map(
            value => ({
              test: value,
              toPerform: false,
              fakeId: generateId()
            })
          );
        }
      }

      const species = data.species || patientInfo.species;
      const procedures = data.procedures || patientInfo.procedures;

      const hasSpecies = species != null;
      const hasDentalProcedures = (procedures || []).some(procedure =>
        DENTAL_PROCEDURES.includes(procedure)
      );
      const isCopied = createdFrom && generalAssessment && dentalChartings;

      // Init Dental Charting tab if species & procedure are selected.
      if (hasSpecies && hasDentalProcedures && !shouldSync && !isCopied) {
        // eslint-disable-next-line
        // @ts-ignore
        state[_id].data.client.patient.generalAssessment =
          state[_id].data.client.patient.generalAssessment === null
            ? EMPTY_GENERAL_ASSESSMENT
            : {
                ...state[_id].data.client.patient.generalAssessment,
                ...getEmptyBoneLoss()
              };
      }

      if (
        data.species === null ||
        (data.procedures &&
          !data.procedures.some(procedure =>
            DENTAL_PROCEDURES.includes(procedure)
          ) &&
          !createdFrom &&
          !shouldSync)
      ) {
        state[_id].data.client.patient.generalAssessment = null;
      }

      const tploRemoved =
        data.procedures && !data.procedures.includes(TPLO_PROCEDURE);

      const tploAdded =
        data.procedures && data.procedures.includes(TPLO_PROCEDURE);

      if (tploAdded && !shouldSync) {
        state[
          _id
        ].data.client.patient.tibialPlateauLevelingOsteotomyPlanning = {
          d1: null,
          d2: null,
          d3: null,
          initialTpa: null,
          postOpTpa: null,
          tploBladeSize: null,
          tploPlateSizeAndModel: null,
          fakeId: generateId()
        };
      }

      if (tploRemoved && !shouldSync) {
        state[
          _id
        ].data.client.patient.tibialPlateauLevelingOsteotomyPlanning = null;
      }
    },

    updateClientInfo(
      state,
      { payload: { _id, data } }: PayloadAction<Types.UpdateClientInfoPayload>
    ) {
      state[_id].data.client = {
        ...state[_id].data.client,
        ...data
      };
    },

    updateAuthorization(
      state,
      { payload }: PayloadAction<Types.UpdateAuthorizationPayload>
    ) {
      const indx = state[
        payload._id
      ].data.authorizations.findIndex(authorization =>
        payload.data.fakeId
          ? payload.data.fakeId === authorization.fakeId
          : payload.data._id === authorization._id
      );
      if (indx !== -1) {
        state[payload._id].data.authorizations[indx].selected =
          payload.data.selected;
      }
    },

    updateDropOff(
      state,
      { payload }: PayloadAction<Types.UpdateDropOffPayload>
    ) {
      state[payload._id].data.client.patient.dropOff = {
        ...state[payload._id].data.client.patient.dropOff,
        ...payload.data
      };

      if (payload.data.doctor !== undefined) {
        state[payload._id].data.client.patient.diagnosticTesting.doctor =
          payload.data.doctor;
      }
    },

    updateClientDisclaimer(
      state,
      {
        payload: { _id, data }
      }: PayloadAction<Types.UpdateClientDisclaimerPayload>
    ) {
      state[_id].data.client.disclaimer = {
        ...state[_id].data.client.disclaimer,
        ...data
      };
    },

    updateDisclaimerProcedure(
      state,
      {
        payload: { _id, data }
      }: PayloadAction<Types.UpdateDisclaimerProcedurePayload>
    ) {
      const indx = state[_id].data.disclaimerProcedures.findIndex(procedure =>
        procedure._id
          ? procedure._id === data.procedureId
          : procedure.fakeId === data.procedureId
      );
      if (indx !== -1) {
        state[_id].data.disclaimerProcedures = state[
          _id
        ].data.disclaimerProcedures.map((procedure, i) =>
          i === indx
            ? { ...procedure, selected: true }
            : { ...procedure, selected: false }
        );
      }
    },

    setVaccinesAndTests(
      state,
      { payload }: PayloadAction<Types.SetVaccinesAndTestsPayload>
    ) {
      state[payload._id].data.client.patient.vaccines = payload.data.vaccines;
      state[payload._id].data.client.patient.patientTests =
        payload.data.patientTests;
      state[payload._id].data.client.patient.treatment = payload.data.treatment;
    },

    updateVaccines(
      state,
      { payload }: PayloadAction<Types.UpdateVaccinesPayload>
    ) {
      payload.data.forEach(newVaccine => {
        const indx = state[
          payload._id
        ].data.client.patient.vaccines.findIndex(oldVaccine =>
          oldVaccine.fakeId
            ? oldVaccine.fakeId === newVaccine.fakeId
            : oldVaccine._id === newVaccine._id
        );
        if (indx !== -1) {
          state[payload._id].data.client.patient.vaccines[indx].toPerform =
            newVaccine.toPerform;
        }
      });
    },

    updatePatientTests(
      state,
      { payload }: PayloadAction<Types.UpdatePatientTestsPayload>
    ) {
      payload.data.forEach(newTest => {
        const indx = state[
          payload._id
        ].data.client.patient.patientTests.findIndex(oldTest =>
          oldTest.fakeId
            ? oldTest.fakeId === newTest.fakeId
            : oldTest._id === newTest._id
        );
        if (indx !== -1) {
          state[payload._id].data.client.patient.patientTests[indx].toPerform =
            newTest.toPerform;
        }
      });
    },

    updateTreatment(
      state,
      { payload: { data, _id } }: PayloadAction<Types.UpdateTreatmentPayload>
    ) {
      if (data === null) {
        state[_id].data.client.patient.treatment = null;
      } else {
        state[_id].data.client.patient.treatment = {
          ...state[_id].data.client.patient.treatment,
          ...(data as any)
        };
      }
    },

    updateExamNotes(
      state,
      {
        payload: {
          _id,
          data: { lastMealAt, ...data }
        }
      }: PayloadAction<Types.UpdateExamNotesPayload>
    ) {
      state[_id].data.client.patient.examNote = {
        ...state[_id].data.client.patient.examNote,
        ...data
      };
      if (lastMealAt !== undefined) {
        state[_id].data.client.patient.examNote.lastMealAt =
          lastMealAt === null ? null : formatISO(new Date(lastMealAt));
      }
    },

    updatePreAnestheticExam(
      state,
      { payload }: PayloadAction<Types.UpdatePreAnestheticExamPayload>
    ) {
      state[payload._id].data.client.patient.preAnestheticExam = {
        ...state[payload._id].data.client.patient.preAnestheticExam,
        ...payload.data
      };
    },

    updatePhysicalExam(
      state,
      { payload }: PayloadAction<Types.UpdatePhysicalExamPayload>
    ) {
      const data = Object.entries(payload.data).reduce((acc, [key, value]) => {
        if (!key.endsWith("AbnormalStateComments")) {
          return {
            ...acc,
            [key]: value,
            ...(value !== PatientStateEnum.Abnormal
              ? { [`${key}AbnormalStateComments`]: null }
              : null)
          };
        }
        return { ...acc, [key]: value };
      }, {});

      state[payload._id].data.client.patient.physicalExam = {
        ...state[payload._id].data.client.patient.physicalExam,
        ...data
      };
    },

    updateAdditionalTests(
      state,
      { payload }: PayloadAction<Types.UpdateAdditionalTestsPayload>
    ) {
      state[
        payload._id
      ].data.client.patient.diagnosticTesting.additionalTests = [
        ...state[payload._id].data.client.patient.diagnosticTesting
          .additionalTests,
        ...payload.data
      ];
    },

    updateAdditionalTest(
      state,
      { payload }: PayloadAction<Types.UpdateAdditionalCasePayload>
    ) {
      const indxOfTest = state[
        payload._id
      ].data.client.patient.diagnosticTesting.additionalTests.findIndex(test =>
        payload.data.fakeId
          ? payload.data.fakeId === test.fakeId
          : payload.data._id === test._id
      );

      if (indxOfTest !== -1) {
        state[
          payload._id
        ].data.client.patient.diagnosticTesting.additionalTests[
          indxOfTest
        ].toPerform = payload.data.toPerform;
      }
    },

    updateDiagnosticTesting(
      state,
      { payload }: PayloadAction<Types.UpdateDiagnosticTestingPayload>
    ) {
      state[payload._id].data.client.patient.diagnosticTesting = {
        ...state[payload._id].data.client.patient.diagnosticTesting,
        ...payload.data
      };

      if (payload.data.doctor !== undefined) {
        state[payload._id].data.client.patient.dropOff.doctor =
          payload.data.doctor;
      }
    },

    addProtocolToCalculations(
      state,
      {
        payload: { _id, data }
      }: PayloadAction<Types.AddProtocolToCalculationsPayload>
    ) {
      const { weight } = state[_id].data.client.patient;

      const newMedications = data.medicationCalculations.map(med => ({
        totalAmount: (weight || 0) * med.dose,
        totalVolume: med.concentration
          ? ((weight || 0) * med.dose) / med.concentration
          : 0,
        ...med
      }));
      const newRegionalBlocks = data.regionalBlocks.map(block => ({
        dosageMin: block.doseMin * (weight || 0),
        dosageMax: block.doseMax * (weight || 0),
        volumeRangeMin: block.concentration
          ? (block.doseMin * (weight || 0)) / block.concentration
          : 0,
        volumeRangeMax: block.concentration
          ? (block.doseMax * (weight || 0)) / block.concentration
          : 0,
        ...block
      }));

      state[_id].data.client.patient.medicationCalculations = [
        ...state[_id].data.client.patient.medicationCalculations,
        ...newMedications
      ];
      state[_id].data.client.patient.regionalBlocks = [
        ...state[_id].data.client.patient.regionalBlocks,
        ...newRegionalBlocks
      ];
    },

    addMedicationCalculation(
      state,
      {
        payload: {
          _id,
          data: { dose, ...data }
        }
      }: PayloadAction<Types.AddMedicationCalculationPayload>
    ) {
      const { weight } = state[_id].data.client.patient;

      const totalAmount = (weight || 0) * dose;
      const totalVolume = data.concentration
        ? totalAmount / data.concentration
        : 0;

      state[_id].data.client.patient.medicationCalculations = [
        ...state[_id].data.client.patient.medicationCalculations,
        { totalAmount, totalVolume, dose, ...data }
      ];
    },

    deleteMedicationCalculations(
      state,
      { payload }: PayloadAction<Types.DeleteMedicationCalculationsPayload>
    ) {
      state[payload._id].data.client.patient.medicationCalculations = state[
        payload._id
      ].data.client.patient.medicationCalculations.filter(calc => {
        const id = calc.fakeId || calc._id;
        return id ? !payload.data.includes(id) : null;
      });
    },

    updateMedicationCalculation(
      state,
      {
        payload: { _id, data }
      }: PayloadAction<Types.UpdateMedicationCalculationPayload>
    ) {
      const indx = state[
        _id
      ].data.client.patient.medicationCalculations.findIndex(calc =>
        calc.fakeId ? calc.fakeId === data.fakeId : calc._id === data._id
      );
      const { weight } = state[_id].data.client.patient;

      if (indx !== -1) {
        // eslint-disable-next-line
        // @ts-ignore
        state[_id].data.client.patient.medicationCalculations[indx] = {
          ...state[_id].data.client.patient.medicationCalculations[indx],
          ...data
        };

        const currentMed =
          state[_id].data.client.patient.medicationCalculations[indx];

        if (data.dose !== undefined && weight && !data.totalAmount) {
          state[_id].data.client.patient.medicationCalculations[
            indx
          ].totalAmount = weight * data.dose;
        }

        state[_id].data.client.patient.medicationCalculations[
          indx
        ].totalVolume = currentMed.concentration
          ? (currentMed.totalAmount || 0) / currentMed.concentration
          : 0;
      }
    },

    updateMiscellaneous(
      state,
      { payload }: PayloadAction<Types.UpdateMiscellaneousPayload>
    ) {
      state[payload._id].data.client.patient.miscellaneousGroup = {
        ...state[payload._id].data.client.patient.miscellaneousGroup,
        ...payload.data
      };
    },

    deleteDefibMedications(
      state,
      {
        payload: { _id, data: ids }
      }: PayloadAction<Types.DeleteDefibMedicationsPayload>
    ) {
      state[_id].data.client.patient.defibMedications = state[
        _id
      ].data.client.patient.defibMedications.filter(med => {
        const id = med.fakeId || med._id;
        return id ? !ids.includes(id) : null;
      });
    },

    addDefibMedication(
      state,
      { payload: { _id, data } }: PayloadAction<Types.AddDefibMedicationPayload>
    ) {
      const { weight } = state[_id].data.client.patient;
      const dosageMax = (weight || 0) * data.doseMax;
      const dosageMin = (weight || 0) * data.doseMin;
      state[_id].data.client.patient.defibMedications = [
        ...state[_id].data.client.patient.defibMedications,
        { ...data, dosageMax, dosageMin }
      ];
    },

    updateDefibMedication(
      state,
      {
        payload: { _id, data }
      }: PayloadAction<Types.UpdateDefibMedicationPayload>
    ) {
      const indx = state[
        _id
      ].data.client.patient.defibMedications.findIndex(calc =>
        calc.fakeId ? calc.fakeId === data.fakeId : calc._id === data._id
      );
      const { weight } = state[_id].data.client.patient;

      if (indx !== -1) {
        // eslint-disable-next-line @typescript-eslint/ban-ts-comment
        // @ts-ignore
        state[_id].data.client.patient.defibMedications[indx] = {
          ...state[_id].data.client.patient.defibMedications[indx],
          ...data
        };

        if (data.doseMin !== undefined && weight) {
          state[_id].data.client.patient.defibMedications[indx].dosageMin =
            data.doseMin * weight;
        }

        if (data.doseMax !== undefined && weight) {
          state[_id].data.client.patient.defibMedications[indx].dosageMax =
            data.doseMax * weight;
        }
      }
    },

    createFluidBolus(
      state,
      { payload: { _id, data } }: PayloadAction<Types.CreateFluidBolusPayload>
    ) {
      const { weight } = state[_id].data.client.patient;
      state[_id].data.client.patient.fluidBolus = {
        ...data,
        ...(weight ? { totalAmount: weight * 10 } : (null as any))
      };
    },

    updateFluidBolus(
      state,
      { payload: { _id, data } }: PayloadAction<Types.UpdateFluidBolusPayload>
    ) {
      state[_id].data.client.patient.fluidBolus = {
        ...state[_id].data.client.patient.fluidBolus,
        ...(data as any)
      };
    },
    deleteFluidBolus(
      state,
      { payload }: PayloadAction<Types.DeleteFluidBolusPayload>
    ) {
      state[payload._id].data.client.patient.fluidBolus = null;
    },

    updateRegionalBlock(
      state,
      {
        payload: { _id, data }
      }: PayloadAction<Types.UpdateRegionalBlockPayload>
    ) {
      const indx = state[
        _id
      ].data.client.patient.regionalBlocks.findIndex(block =>
        block.fakeId ? block.fakeId === data.fakeId : block._id === data._id
      );
      const { weight } = state[_id].data.client.patient;

      if (indx !== -1) {
        // eslint-disable-next-line @typescript-eslint/ban-ts-comment
        // @ts-ignore
        state[_id].data.client.patient.regionalBlocks[indx] = {
          ...state[_id].data.client.patient.regionalBlocks[indx],
          ...data
        };

        const { concentration, dosageMin, dosageMax } = state[
          _id
        ].data.client.patient.regionalBlocks[indx];

        if (data.doseMin !== undefined && weight) {
          state[_id].data.client.patient.regionalBlocks[indx].dosageMin =
            data.doseMin * weight;
          state[_id].data.client.patient.regionalBlocks[
            indx
          ].volumeRangeMin = concentration
            ? (data.doseMin * weight) / concentration
            : 0;
        }

        if (data.doseMax !== undefined && weight) {
          state[_id].data.client.patient.regionalBlocks[indx].dosageMax =
            data.doseMax * weight;
          state[_id].data.client.patient.regionalBlocks[
            indx
          ].volumeRangeMax = concentration
            ? (data.doseMax * weight) / concentration
            : 0;
        }

        if (data.dosageMin != null) {
          state[_id].data.client.patient.regionalBlocks[
            indx
          ].volumeRangeMin = concentration ? data.dosageMin / concentration : 0;
        }

        if (data.dosageMax != null) {
          state[_id].data.client.patient.regionalBlocks[
            indx
          ].volumeRangeMax = concentration ? data.dosageMax / concentration : 0;
        }

        if (
          data.concentration !== undefined &&
          dosageMin != null &&
          dosageMax != null
        ) {
          state[_id].data.client.patient.regionalBlocks[
            indx
          ].volumeRangeMin = data.concentration
            ? dosageMin / data.concentration
            : 0;
          state[_id].data.client.patient.regionalBlocks[
            indx
          ].volumeRangeMax = data.concentration
            ? dosageMax / data.concentration
            : 0;
        }
      }
    },
    deleteRegionalBlocks(
      state,
      {
        payload: { _id, data: ids }
      }: PayloadAction<Types.DeleteRegionalBlocksPayload>
    ) {
      state[_id].data.client.patient.regionalBlocks = state[
        _id
      ].data.client.patient.regionalBlocks.filter(block => {
        const id = block.fakeId || block._id;
        return id ? !ids.includes(id) : null;
      });
    },
    addRegionalBlock(
      state,
      { payload: { _id, data } }: PayloadAction<Types.AddRegionalBlockPayload>
    ) {
      const { weight } = state[_id].data.client.patient;

      const dosageMin = data.doseMin * (weight || 0);
      const dosageMax = data.doseMax * (weight || 0);
      const volumeRangeMin = data.concentration
        ? dosageMin / data.concentration
        : 0;
      const volumeRangeMax = data.concentration
        ? dosageMax / data.concentration
        : 0;

      state[_id].data.client.patient.regionalBlocks = [
        ...state[_id].data.client.patient.regionalBlocks,
        {
          ...data,
          doseMax: data.doseMax,
          doseMin: data.doseMin,
          dosageMin,
          dosageMax,
          volumeRangeMax,
          volumeRangeMin
        }
      ];
    },

    updateCalculationAgreement(
      state,
      {
        payload: { _id, data }
      }: PayloadAction<Types.UpdateCalculationAgreementPayload>
    ) {
      // eslint-disable-next-line @typescript-eslint/ban-ts-comment
      // @ts-ignore
      state[_id].data.client.patient.calculationAgreement = {
        ...state[_id].data.client.patient.calculationAgreement,
        ...data
      };
    },

    addTablet(
      state,
      { payload: { _id, data } }: PayloadAction<Types.AddTabletPayload>
    ) {
      const { weight } = state[_id].data.client.patient;

      const dosage = (weight || 0) * data.dose;
      state[_id].data.client.patient.tablets = [
        ...state[_id].data.client.patient.tablets,
        { ...data, dose: data.dose, dosage }
      ];
    },
    deleteTablets(
      state,
      { payload: { _id, data: ids } }: PayloadAction<Types.DeleteTabletsPayload>
    ) {
      state[_id].data.client.patient.tablets = state[
        _id
      ].data.client.patient.tablets.filter(tablet => {
        const id = tablet.fakeId || tablet._id;
        return id ? !ids.includes(id) : null;
      });
    },
    updateTablet(
      state,
      { payload: { _id, data } }: PayloadAction<Types.UpdateTabletPayload>
    ) {
      const indx = state[_id].data.client.patient.tablets.findIndex(tablet =>
        tablet.fakeId ? tablet.fakeId === data.fakeId : tablet._id === data._id
      );
      if (indx !== -1) {
        state[_id].data.client.patient.tablets[indx] = {
          ...state[_id].data.client.patient.tablets[indx],
          ...(data as any)
        };

        const { weight } = state[_id].data.client.patient;

        if (data.dose !== undefined && weight) {
          state[_id].data.client.patient.tablets[indx].dosage =
            data.dose * weight;
        }
      }
    },
    addSuspension(
      state,
      { payload: { _id, data } }: PayloadAction<Types.AddSuspensionPayload>
    ) {
      state[_id].data.client.patient.suspensions = [
        ...state[_id].data.client.patient.suspensions,
        {
          ...data
        }
      ];
    },
    deleteSuspensions(
      state,
      {
        payload: { _id, data: ids }
      }: PayloadAction<Types.DeleteSuspensionsPayload>
    ) {
      state[_id].data.client.patient.suspensions = state[
        _id
      ].data.client.patient.suspensions.filter(susp => {
        const id = susp.fakeId || susp._id;
        return id ? !ids.includes(id) : null;
      });
    },
    updateSuspension(
      state,
      { payload: { _id, data } }: PayloadAction<Types.UpdateSuspensionPayload>
    ) {
      const indx = state[_id].data.client.patient.suspensions.findIndex(susp =>
        susp.fakeId ? susp.fakeId === data.fakeId : susp._id === data._id
      );
      if (indx !== -1) {
        state[_id].data.client.patient.suspensions[indx] = {
          ...state[_id].data.client.patient.suspensions[indx],
          ...(data as any)
        };

        const { weight } = state[_id].data.client.patient;
        const { concentration, dose } = state[
          _id
        ].data.client.patient.suspensions[indx];

        if (data.dose !== undefined && weight) {
          state[_id].data.client.patient.suspensions[
            indx
          ].volume = concentration ? (data.dose * weight) / concentration : 0;
        }

        if (data.concentration !== undefined && weight) {
          state[_id].data.client.patient.suspensions[
            indx
          ].volume = data.concentration
            ? (dose * weight) / data.concentration
            : 0;
        }
      }
    },
    updateTPLO(
      state,
      { payload: { _id, data } }: PayloadAction<Types.UpdateTPLOPayload>
    ) {
      if (data === null) {
        state[
          _id
        ].data.client.patient.tibialPlateauLevelingOsteotomyPlanning = null;
      } else {
        state[
          _id
        ].data.client.patient.tibialPlateauLevelingOsteotomyPlanning = {
          ...state[_id].data.client.patient
            .tibialPlateauLevelingOsteotomyPlanning,
          ...(data as any)
        };
      }
    },
    addPostAnestheticVital(
      state,
      {
        payload: { _id, data }
      }: PayloadAction<Types.AddPostAnestheticVitalPayload>
    ) {
      state[_id].data.client.patient.postAnestheticVitals = [
        ...state[_id].data.client.patient.postAnestheticVitals,
        data
      ];
    },
    updatePostAnestheticVital(
      state,
      {
        payload: { _id, data }
      }: PayloadAction<Types.UpdatePostAnestheticVitalPayload>
    ) {
      const indx = state[
        _id
      ].data.client.patient.postAnestheticVitals.findIndex(block =>
        block.fakeId ? block.fakeId === data.fakeId : block._id === data._id
      );
      if (indx !== -1) {
        state[_id].data.client.patient.postAnestheticVitals[indx] = {
          ...state[_id].data.client.patient.postAnestheticVitals[indx],
          ...(data as any)
        };
      }
    },
    updatePostAnestheticVitalComment(
      state,
      {
        payload: { _id, data }
      }: PayloadAction<Types.UpdatePostAnestheticVitalCommentPayload>
    ) {
      // eslint-disable-next-line
      // @ts-ignore
      state[_id].data.client.patient.postAnestheticVitalComment = {
        ...state[_id].data.client.patient.postAnestheticVitalComment,
        ...data
      };
    },
    addPatientProcedure(
      state,
      {
        payload: { _id, data }
      }: PayloadAction<Types.AddPatientProcedureBlockPayload>
    ) {
      state[_id].data.client.patient.patientProcedures = [
        ...state[_id].data.client.patient.patientProcedures,
        data
      ];
    },
    updatePatientProcedure(
      state,
      {
        payload: { _id, data }
      }: PayloadAction<Types.UpdatePatientProcedureBlockPayload>
    ) {
      const anesthesia = state[_id].data.client.patient.patientProcedures.find(
        procedure => procedure.name === "Anesthesia"
      );
      const indx = state[
        _id
      ].data.client.patient.patientProcedures.findIndex(block =>
        block.fakeId ? block.fakeId === data.fakeId : block._id === data._id
      );
      if (indx !== -1 && anesthesia) {
        state[_id].data.client.patient.patientProcedures[indx] = {
          ...state[_id].data.client.patient.patientProcedures[indx],
          ...(data as any)
        };

        const isAnesthesiaUpdated = data._id
          ? data._id === anesthesia._id
          : data.fakeId === anesthesia.fakeId;

        if (data.startTime && !isAnesthesiaUpdated && !anesthesia.startTime) {
          anesthesia.startTime = data.startTime;
        }

        if (data.endTime && isAnesthesiaUpdated) {
          state[_id].data.client.patient.patientProcedures = state[
            _id
          ].data.client.patient.patientProcedures.map(procedure => ({
            ...procedure,
            endTime:
              procedure.endTime != null ? procedure.endTime : data.endTime
          }));
        }
      }
    },
    addFluidAndTemperature(
      state,
      {
        payload: { _id, data }
      }: PayloadAction<Types.AddFluidAndTemperatureBlockPayload>
    ) {
      state[_id].data.client.patient.fluidTemperatures = [
        ...state[_id].data.client.patient.fluidTemperatures,
        data
      ];
    },
    updateFluidAndTemperature(
      state,
      {
        payload: { _id, data }
      }: PayloadAction<Types.UpdateFluidAndTemperatureBlockPayload>
    ) {
      const indx = state[
        _id
      ].data.client.patient.fluidTemperatures.findIndex(block =>
        block.fakeId ? block.fakeId === data.fakeId : block._id === data._id
      );
      if (indx !== -1) {
        state[_id].data.client.patient.fluidTemperatures[indx] = {
          ...state[_id].data.client.patient.fluidTemperatures[indx],
          ...(data as any)
        };
      }
    },
    addAnestheticGases(
      state,
      {
        payload: { _id, data }
      }: PayloadAction<Types.AddAnestheticGasesBlockPayload>
    ) {
      state[_id].data.client.patient.anestheticGases = [
        ...state[_id].data.client.patient.anestheticGases,
        data
      ];
    },
    updateAnestheticGases(
      state,
      {
        payload: { _id, data }
      }: PayloadAction<Types.UpdateAnestheticGasesBlockPayload>
    ) {
      const indx = state[
        _id
      ].data.client.patient.anestheticGases.findIndex(block =>
        block.fakeId ? block.fakeId === data.fakeId : block._id === data._id
      );
      if (indx !== -1) {
        state[_id].data.client.patient.anestheticGases[indx] = {
          ...state[_id].data.client.patient.anestheticGases[indx],
          ...(data as any)
        };
      }
    },
    addVitals(
      state,
      { payload: { _id, data } }: PayloadAction<Types.AddVitalsBlockPayload>
    ) {
      state[_id].data.client.patient.vitals = [
        ...state[_id].data.client.patient.vitals,
        data
      ];
    },
    updateVitals(
      state,
      { payload: { _id, data } }: PayloadAction<Types.UpdateVitalsBlockPayload>
    ) {
      const indx = state[_id].data.client.patient.vitals.findIndex(block =>
        block.fakeId ? block.fakeId === data.fakeId : block._id === data._id
      );
      if (indx !== -1) {
        state[_id].data.client.patient.vitals[indx] = {
          ...state[_id].data.client.patient.vitals[indx],
          ...(data as any)
        };
      }
    },
    updateVitalComments(
      state,
      {
        payload: { _id, data }
      }: PayloadAction<Types.UpdateVitalCommentBlockPayload>
    ) {
      state[_id].data.client.patient.vitalComment = {
        ...state[_id].data.client.patient.vitalComment,
        ...(data as any)
      };
    },
    updateMonitoring(
      state,
      {
        payload: { _id, data }
      }: PayloadAction<Types.UpdateMonitoringBlockPayload>
    ) {
      state[_id].data.client.patient.monitoring = {
        ...state[_id].data.client.patient.monitoring,
        ...(data as any)
      };
    },
    updateGeneralAssessment(
      state,
      {
        payload: { _id, data }
      }: PayloadAction<Types.UpdateGeneralAssessmentPayload>
    ) {
      if (data === null) {
        state[_id].data.client.patient.generalAssessment = null;
      } else {
        // eslint-disable-next-line
        // @ts-ignore
        state[_id].data.client.patient.generalAssessment = {
          ...state[_id].data.client.patient.generalAssessment,
          ...data
        };
      }

      const abnormalBoneLossTeeth = [
        ...(data?.between25And50PerCentBoneLoss || []),
        ...(data?.greaterThan50PerCentBoneLoss || []),
        ...(data?.lessThan25PerCentBoneLoss || []),
        ...(data?.retainedToothRoots || []),
        ...(data?.internalToothResorption || []),
        ...(data?.externalInflammatoryToothResorption || []),
        ...(data?.externalReplacementRootResorption || []),
        ...(data?.externalSurfaceRootResorption || []),
        ...(data?.externalCervicalToothResorption || []),
        ...(data?.toothResorptionType1 || []),
        ...(data?.toothResorptionType2 || []),
        ...(data?.toothResorptionType3 || []),
        ...(data?.periapicalLucency || []),
        ...(data?.periapicalRarefaction || []),
        ...(data?.widenedPulpCanal || [])
      ]
        .map(Number)
        .filter(Boolean);

      const missingBoneLossTeeth =
        data?.missingTeeth && data.missingTeeth[0] !== "Remaining dentition"
          ? data.missingTeeth.map(Number)
          : [];

      const uneruptedBoneLossTeeth =
        data?.uneruptedTeeth && data.uneruptedTeeth[0] !== "Remaining dentition"
          ? data.uneruptedTeeth.map(Number)
          : [];

      const dentalCharting =
        state[_id].data.client.patient.dentalChartings || [];
      abnormalBoneLossTeeth.forEach(toothNumber => {
        const indxOfChart = dentalCharting.findIndex(
          chart => chart.tooth === toothNumber
        );
        if (indxOfChart !== -1) {
          dentalCharting[indxOfChart].toothState = ToothStateEnum.Abnormal;
        }
      });
      missingBoneLossTeeth.forEach(toothNumber => {
        const indxOfChart = dentalCharting.findIndex(
          chart => chart.tooth === toothNumber
        );
        if (indxOfChart !== -1) {
          dentalCharting[indxOfChart].toothState = ToothStateEnum.Missing;
        }
      });
      uneruptedBoneLossTeeth.forEach(toothNumber => {
        const indxOfChart = dentalCharting.findIndex(
          chart => chart.tooth === toothNumber
        );
        if (indxOfChart !== -1) {
          dentalCharting[indxOfChart].toothState = ToothStateEnum.Unerupted;
        }
      });

      const { shouldSync } = state[_id];

      if (data?.dentalChart !== undefined && !shouldSync) {
        state[_id].data.client.patient.dentalSketchComments = [];
        state[_id].data.client.patient.dentalSketch = null;
        state[_id].data.client.patient.generalAssessment = {
          ...state[_id].data.client.patient.generalAssessment,
          ...getEmptyBoneLoss()
        } as any;

        const species = state[_id].data.client.patient.species || null;
        const milkOrAdultTeeth =
          state[_id].data.client.patient.generalAssessment?.dentalChart || null;

        state[_id].data.client.patient.dentalChartings =
          data.dentalChart === null
            ? null
            : getSpeciesTeeth(species, milkOrAdultTeeth).map(number => ({
                fakeId: generateId(),
                tooth: number,
                ...EMPTY_DENTAL_CHARTING
              }));
      }
    },
    setDentalChartings(
      state,
      { payload: { _id, data } }: PayloadAction<Types.SetDentalChartingsPayload>
    ) {
      state[_id].data.client.patient.dentalChartings = data;
    },
    updateTeethState(
      state,
      {
        payload: {
          _id,
          data: { ids, state: toothState }
        }
      }: PayloadAction<Types.UpdateTeethStatePayload>
    ) {
      const dentalCharting = state[_id].data.client.patient.dentalChartings;
      if (!dentalCharting) {
        return;
      }
      ids.forEach(idOrFakeId => {
        const indxOfChart = dentalCharting.findIndex(chart => {
          const chartId = chart._id || chart.fakeId;
          return chartId === idOrFakeId;
        });
        if (indxOfChart !== -1) {
          dentalCharting[indxOfChart].toothState = toothState;
        }
      });
    },

    updateTeethPhoto(
      state,
      { payload: { _id, data } }: PayloadAction<Types.UpdateTeethPhotosPayload>
    ) {
      // eslint-disable-next-line
      // @ts-ignore
      state[_id].data.client.patient.generalAssessment = {
        ...state[_id].data.client.patient.generalAssessment,
        ...data
      };
    },

    updateDentalChart(
      state,
      {
        payload: {
          _id,
          data: { ids, ...chartData }
        }
      }: PayloadAction<Types.UpdateDentalChartPayload>
    ) {
      const charts = state[_id].data.client.patient.dentalChartings;
      if (!charts) {
        return;
      }
      ids.forEach(id => {
        const indx = charts.findIndex(
          chart => chart._id === id || chart.fakeId === id
        );
        if (indx === -1) {
          return;
        }

        // eslint-disable-next-line
        // @ts-ignore
        state[_id].data.client.patient.dentalChartings[indx] = {
          ...charts[indx],
          ...chartData
        };

        const currentChart = charts[indx];
        const species = state[_id].data.client.patient.species || null;

        if (!isToothFilled(currentChart)) {
          currentChart.toothState = null;
          return;
        }

        if (currentChart.toothState !== null) {
          return;
        }

        const isToothTreated =
          chartData.endodontics?.length > 0 ||
          chartData.restorations?.length > 0 ||
          !!chartData.obturation ||
          !!chartData.distalWorkingLength ||
          !!chartData.mesialWorkingLength ||
          !!chartData.palatalWorkingLength ||
          !!chartData.distalMarFile ||
          !!chartData.mesialMarFile ||
          !!chartData.palatalMarFile ||
          !!chartData.technique;

        const isToothExtracted = chartData.oralSurgeries?.length > 0;

        if (isToothTreated) {
          currentChart.toothState = ToothStateEnum.Treated;
          return;
        }

        if (isToothExtracted) {
          currentChart.toothState = ToothStateEnum.Extracted;
          return;
        }

        currentChart.toothState = hasToothDeepPockets(currentChart, species)
          ? ToothStateEnum.Abnormal
          : ToothStateEnum.Untreated;
      });
    },
    incrementDentalTimer(
      state,
      {
        payload: {
          _id,
          data: { increment, toothNumber }
        }
      }: PayloadAction<Types.IncrementDentalTimerPayload>
    ) {
      const charting = state[_id].data.client.patient.dentalChartings;
      if (!charting) {
        return;
      }

      const chartIndex = charting.findIndex(
        chart => chart.tooth === toothNumber
      );

      if (chartIndex !== -1) {
        const { timerDurationSec } = charting[chartIndex];
        charting[chartIndex].timerDurationSec = timerDurationSec + increment;
      }
    },
    addCurrentMedication(
      state,
      {
        payload: { _id, data }
      }: PayloadAction<Types.AddCurrentMedicationPayload>
    ) {
      state[_id].data.client.patient.currentMedications = [
        ...state[_id].data.client.patient.currentMedications,
        data
      ];
    },
    updateCurrentMedication(
      state,
      {
        payload: { _id, data }
      }: PayloadAction<Types.UpdateCurrentMedicationPayload>
    ) {
      const index = state[
        _id
      ].data.client.patient.currentMedications.findIndex(med =>
        data._id ? data._id === med._id : data.fakeId === med.fakeId
      );
      if (index !== -1) {
        // eslint-disable-next-line
        // @ts-ignore
        state[_id].data.client.patient.currentMedications[index] = {
          ...state[_id].data.client.patient.currentMedications[index],
          ...data
        };
      }
    },
    deleteCurrentMedications(
      state,
      {
        payload: { _id, data: keys }
      }: PayloadAction<Types.DeleteCurrentMedicationsPayload>
    ) {
      state[_id].data.client.patient.currentMedications = state[
        _id
      ].data.client.patient.currentMedications.filter(
        med => !keys.includes(med._id || med.fakeId || "")
      );
    },
    setDentalSketchComments(
      state,
      {
        payload: { _id, data }
      }: PayloadAction<Types.SetDentalSketchCommentsPayload>
    ) {
      state[_id].data.client.patient.dentalSketchComments = data;
    },
    addDentalSketchComment(
      state,
      {
        payload: { _id, data }
      }: PayloadAction<Types.AddDentalSketchCommentPayload>
    ) {
      state[_id].data.client.patient.dentalSketchComments = [
        ...state[_id].data.client.patient.dentalSketchComments,
        data
      ];
    },
    updateDentalSketchComment(
      state,
      {
        payload: { _id, data }
      }: PayloadAction<Types.UpdateDentalSketchCommentPayload>
    ) {
      const index = state[
        _id
      ].data.client.patient.dentalSketchComments.findIndex(comment =>
        data._id ? data._id === comment._id : data.fakeId === comment.fakeId
      );
      if (index !== -1) {
        // eslint-disable-next-line
        // @ts-ignore
        state[_id].data.client.patient.dentalSketchComments[index] = {
          ...state[_id].data.client.patient.dentalSketchComments[index],
          ...data
        };
      }
    },
    deleteDentalSketchComments(
      state,
      {
        payload: { _id, data: ids }
      }: PayloadAction<Types.DeleteDentalSketchCommentsPayload>
    ) {
      state[_id].data.client.patient.dentalSketchComments = state[
        _id
      ].data.client.patient.dentalSketchComments.filter(comment => {
        const id = comment.fakeId || comment._id;
        return id ? !ids.includes(id) : null;
      });
    },
    updateDentalSketch(
      state,
      {
        payload: {
          _id,
          data: { dentalSketch }
        }
      }: PayloadAction<Types.UpdateDentalSketchPayload>
    ) {
      state[_id].data.client.patient = {
        ...state[_id].data.client.patient,
        dentalSketch
      };
    },
    setShouldSync(
      state,
      {
        payload: { _id, data: newShouldSync }
      }: PayloadAction<Types.ShouldSyncPayload>
    ) {
      state[_id].shouldSync = newShouldSync;
    },

    setPublishRequired(
      state,
      { payload: { _id } }: PayloadAction<Types.SetPublishRequiredPayload>
    ) {
      state[_id].status = "has_offline_changes";
    }
  }
});

export const { reducer, actions } = casesSlice;
